import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { TicketTypeIcon, TICKET_TYPES, InlineTypeSelector } from './TicketTypeSelector';
import ConfirmModal from './ConfirmModal';
import api from '../../services/api';
import { useTaskTypes } from '../../hooks/useTaskTypes';
import { validationMessages } from '../../utils/validationMessages';
import './SubtaskSection.css';

// Simple inline searchable dropdown for assignee selection
const InlineAssigneeSelect = ({ options, value, onChange, placeholder = 'Unassigned', disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [dropdownStyle, setDropdownStyle] = useState({});
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const filteredOptions = options.filter(opt => 
    opt.label.toLowerCase().includes(search.toLowerCase())
  );

  const selectedOption = options.find(opt => opt.value === value);

  // Calculate dropdown position
  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setDropdownStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 200),
      zIndex: 99999
    });
  }, []);

  const handleToggle = useCallback(() => {
    if (disabled) return;
    if (!isOpen) {
      updatePosition();
    }
    setIsOpen(prev => !prev);
    setSearch('');
  }, [disabled, isOpen, updatePosition]);

  const handleSelect = useCallback((optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearch('');
  }, [onChange]);

  const handleClear = useCallback((e) => {
    e.stopPropagation();
    onChange(null);
  }, [onChange]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (triggerRef.current && !triggerRef.current.contains(e.target) &&
          dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Update position on scroll/resize
  useEffect(() => {
    if (!isOpen) return;
    const handleUpdate = () => updatePosition();
    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);
    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [isOpen, updatePosition]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [isOpen]);

  return (
    <div className={`inline-assignee-select ${isOpen ? 'is-open' : ''} ${disabled ? 'is-disabled' : ''}`}>
      <button 
        ref={triggerRef}
        type="button" 
        className="assignee-trigger" 
        onClick={handleToggle}
        disabled={disabled}
      >
        <span className="assignee-trigger-text">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        {selectedOption && (
          <span className="assignee-clear" onClick={handleClear} title="Clear">×</span>
        )}
        <svg className="assignee-arrow" viewBox="0 0 24 24" fill="currentColor">
          <path d="M7 10l5 5 5-5z" />
        </svg>
      </button>
      
      {isOpen && (
        <div ref={dropdownRef} className="assignee-dropdown" style={dropdownStyle}>
          <div className="assignee-search-wrapper">
            <div className="assignee-search-box">
              <svg className="assignee-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                ref={inputRef}
                type="text"
                className="assignee-search-input"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
          <div className="assignee-options">
            <div 
              className={`assignee-option ${!value ? 'is-selected' : ''}`}
              onClick={() => handleSelect(null)}
            >
              {placeholder}
            </div>
            {filteredOptions.map(opt => (
              <div
                key={opt.value}
                className={`assignee-option ${opt.value === value ? 'is-selected' : ''}`}
                onClick={() => handleSelect(opt.value)}
              >
                {opt.initials && <span className="assignee-avatar">{opt.initials}</span>}
                {opt.label}
              </div>
            ))}
            {filteredOptions.length === 0 && search && (
              <div className="assignee-no-results">No results found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Base hierarchy rules - what system child types are allowed for each parent type
// Custom types are dynamically added based on their is_subtask_type flag
const BASE_HIERARCHY_RULES = {
  epic: ['story', 'task', 'bug'],  // Epic children - no subtask
  task: ['subtask'],               // Task can have subtasks only
  story: ['subtask'],              // Story can have subtasks only
  bug: ['subtask'],                // Bug can have subtasks
  subtask: [],                     // Subtask cannot have children
};

// Build dynamic hierarchy rules that include custom types from API
const buildAllowedChildTypes = (parentType, taskTypes) => {
  // Start with the base rules for known parent types
  const baseTypes = BASE_HIERARCHY_RULES[parentType] || [];
  
  // Check if this is a custom parent type
  const parentConfig = taskTypes[parentType];
  
  // Any parent-capable type with no static base rules — allow subtask-type children
  // This handles both custom types AND system types configured as parent-capable (e.g., bug)
  if (baseTypes.length === 0 && parentConfig && parentConfig.isParentCapable && !parentConfig.isSubtaskType) {
    const subtaskTypeKeys = Object.entries(taskTypes)
      .filter(([, config]) => config.isSubtaskType)
      .map(([key]) => key);
    return subtaskTypeKeys;
  }

  if (baseTypes.length === 0) return [];
  
  // Collect custom type keys from API data
  const customKeys = Object.entries(taskTypes)
    .filter(([, config]) => config.isCustom)
    .map(([key, config]) => ({ key, isSubtaskType: config.isSubtaskType }));
  
  const allAllowed = [...baseTypes];
  
  customKeys.forEach(({ key, isSubtaskType }) => {
    if (parentType === 'epic') {
      // Epic can have non-subtask custom types as children
      if (!isSubtaskType) {
        allAllowed.push(key);
      }
    } else if (['story', 'task'].includes(parentType)) {
      // Story/Task can have subtask-type custom types as children
      if (isSubtaskType) {
        allAllowed.push(key);
      }
    }
  });
  
  return allAllowed;
};

// Status configuration
const STATUSES = {
  backlog: { color: '#6b778c', bgColor: '#f4f5f7', label: 'Backlog' },
  in_progress: { color: '#0052cc', bgColor: '#deebff', label: 'In Progress' },
  review: { color: '#ff8b00', bgColor: '#fff7e6', label: 'Review' },
  done: { color: '#006644', bgColor: '#e3fcef', label: 'Done' },
};

const SubtaskSection = ({
  parentTask,
  children = [],
  onChildCreated,
  projectMembers = [],
  canEdit = true,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newSubtask, setNewSubtask] = useState({
    title: '',
    task_type: 'subtask',
    assignee_id: null,
  });
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Delete',
    variant: 'danger',
    isLoading: false,
    onConfirm: null,
  });
  const titleInputRef = useRef(null);

  // Load task types from API
  const { taskTypes, getTypeByKey } = useTaskTypes(parentTask?.project_id);

  // Get allowed child types based on parent type (dynamic, includes custom types)
  const allowedChildTypes = useMemo(
    () => buildAllowedChildTypes(parentTask?.task_type, taskTypes),
    [parentTask?.task_type, taskTypes]
  );
  const canHaveChildren = allowedChildTypes.length > 0;

  // Focus input when creating
  useEffect(() => {
    if (isCreating && titleInputRef.current) {
      titleInputRef.current.focus();
    }
  }, [isCreating]);

  // Set default type when opening creation form
  useEffect(() => {
    if (isCreating && allowedChildTypes.length > 0) {
      // Epic parents: default to 'story'
      // Story/Task parents: default to 'subtask'
      let defaultType = 'subtask';
      if (parentTask?.task_type === 'epic') {
        defaultType = 'story';
      } else if (allowedChildTypes.includes('subtask')) {
        defaultType = 'subtask';
      } else if (allowedChildTypes.length > 0) {
        defaultType = allowedChildTypes[0];
      }
      setNewSubtask(prev => ({ ...prev, task_type: defaultType }));
    }
  }, [isCreating, allowedChildTypes, parentTask?.task_type]);

  const handleStartCreate = () => {
    setIsCreating(true);
    // Default type based on parent:
    // - Epic parents: default to 'story' (can change to task/bug)
    // - Story/Task parents: default to 'subtask' (can change to bug)
    let defaultType = 'subtask';
    if (parentTask?.task_type === 'epic') {
      defaultType = 'story';
    } else if (allowedChildTypes.includes('subtask')) {
      defaultType = 'subtask';
    } else if (allowedChildTypes.length > 0) {
      defaultType = allowedChildTypes[0];
    }
    setNewSubtask({
      title: '',
      task_type: defaultType,
      assignee_id: null,
    });
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    setNewSubtask({ title: '', task_type: 'subtask', assignee_id: null });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newSubtask.title.trim()) {
      toast.error(validationMessages.task.title.required);
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await api.post(`/projects/${parentTask.project_id}/tasks`, {
        title: newSubtask.title.trim(),
        task_type: newSubtask.task_type,
        parent_id: parentTask.id,
        assignee_id: newSubtask.assignee_id || null,
        priority: 'medium',
        sprint_id: parentTask.sprint_id || null,
      });

      const typeConfig = getTypeByKey(newSubtask.task_type);
      const typeLabel = typeConfig?.label || 'Subtask';
      if (parentTask.sprint_id) {
        toast.success(`${typeLabel} created in parent's sprint`);
      } else {
        toast.success(`${typeLabel} created`);
      }
      setNewSubtask({ title: '', task_type: newSubtask.task_type, assignee_id: null });
      setIsCreating(false);
      
      if (onChildCreated) {
        onChildCreated(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create subtask');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    } else if (e.key === 'Escape') {
      handleCancelCreate();
    }
  };

  const handleDeleteChild = (child) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Task',
      message: `Are you sure you want to delete "${child.task_key}"? This action cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
      isLoading: false,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isLoading: true }));
        try {
          await api.delete(`/tasks/${child.id}`);
          toast.success('Task deleted');
          if (onChildCreated) {
            // Trigger parent refresh - use null to signal a delete/refresh
            onChildCreated(null);
          }
          setConfirmModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
        } catch (error) {
          toast.error('Failed to delete task');
          setConfirmModal(prev => ({ ...prev, isLoading: false }));
        }
      }
    });
  };

  // Calculate progress
  const totalChildren = children.length;
  const completedChildren = children.filter(c => c.status === 'done').length;
  const progressPercent = totalChildren > 0 ? Math.round((completedChildren / totalChildren) * 100) : 0;

  if (!canHaveChildren && children.length === 0) {
    return null;
  }

  return (
    <div className="subtask-section">
      <div className="subtask-header" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="subtask-header-left">
          <button className="expand-toggle" aria-expanded={isExpanded}>
            <svg 
              className={`expand-icon ${isExpanded ? 'expanded' : ''}`} 
              viewBox="0 0 24 24" 
              fill="currentColor"
            >
              <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
            </svg>
          </button>
          <h3 className="subtask-title">
            {parentTask?.task_type === 'epic' ? 'Child Issues' : 'Subtasks'}
          </h3>
          {totalChildren > 0 && (
            <span className="subtask-count">{totalChildren}</span>
          )}
        </div>
        
        {totalChildren > 0 && (
          <div className="subtask-progress" onClick={(e) => e.stopPropagation()}>
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <span className="progress-text">
              {completedChildren}/{totalChildren}
            </span>
          </div>
        )}
      </div>

      {isExpanded && (
        <div className="subtask-content">
          {/* Existing children list */}
          {children.length > 0 && (
            <div className="subtask-list">
              {children.map((child) => (
                <div 
                  key={child.id}
                  className="subtask-item-wrapper"
                >
                  <Link
                    to={`/tasks/${child.task_key}`}
                    className="subtask-item"
                  >
                    <div className="subtask-item-left">
                      <TicketTypeIcon type={child.task_type} size="sm" config={getTypeByKey(child.task_type)} />
                      <span className="subtask-key">{child.task_key}</span>
                      <span className="subtask-item-title">{child.title}</span>
                    </div>
                    <div className="subtask-item-center">
                      {child.assignee && (
                        <div className="subtask-assignee" title={child.assignee.full_name}>
                          {child.assignee.full_name?.split(' ').map(n => n.charAt(0)).join('') || '?'}
                        </div>
                      )}
                      {child.story_points && (
                        <span className="subtask-points">{child.story_points}</span>
                      )}
                      <span 
                        className="subtask-status"
                        style={{
                          backgroundColor: STATUSES[child.status]?.bgColor || '#f4f5f7',
                          color: STATUSES[child.status]?.color || '#6b778c',
                        }}
                      >
                        {STATUSES[child.status]?.label || child.status}
                      </span>
                    </div>
                  </Link>
                  <button
                    className="subtask-delete-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDeleteChild(child);
                    }}
                    title="Delete task"
                    aria-label={`Delete ${child.task_key}`}
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-9l-1 1H5v2h14V4z" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Inline creation form */}
          {isCreating ? (
            <form className="subtask-create-form" onSubmit={handleSubmit}>
              <div className="subtask-create-row">
                {/* Type selector - uses InlineTypeSelector for proper styling */}
                {allowedChildTypes.length > 1 ? (
                  <InlineTypeSelector
                    value={newSubtask.task_type}
                    onChange={(type) => setNewSubtask({ ...newSubtask, task_type: type })}
                    allowedTypes={allowedChildTypes}
                    projectId={parentTask?.project_id}
                    disabled={isSubmitting}
                  />
                ) : (
                  /* Fallback: Fixed type indicator if only one option */
                  <div className="type-indicator" title={getTypeByKey(newSubtask.task_type)?.label || 'Task'}>
                    <TicketTypeIcon type={newSubtask.task_type} size="sm" config={getTypeByKey(newSubtask.task_type)} />
                  </div>
                )}

                {/* Title input */}
                <input
                  ref={titleInputRef}
                  type="text"
                  className="subtask-title-input"
                  placeholder="What needs to be done?"
                  value={newSubtask.title}
                  onChange={(e) => setNewSubtask({ ...newSubtask, title: e.target.value })}
                  onKeyDown={handleKeyDown}
                  disabled={isSubmitting}
                  autoFocus
                />

                {/* Assignee selector (optional) - Inline Searchable */}
                {projectMembers.length > 0 && (
                  <InlineAssigneeSelect
                    options={projectMembers.map(m => ({
                      value: m.user.id,
                      label: m.user.full_name || `${m.user.first_name} ${m.user.last_name}`.trim(),
                      initials: `${(m.user.first_name || '')[0] || ''}${(m.user.last_name || '')[0] || ''}`
                    }))}
                    value={newSubtask.assignee_id}
                    onChange={(assigneeId) => setNewSubtask({ 
                      ...newSubtask, 
                      assignee_id: assigneeId 
                    })}
                    placeholder="Unassigned"
                    disabled={isSubmitting}
                  />
                )}
              </div>

              <div className="subtask-create-actions">
                <button 
                  type="submit" 
                  className="btn-create"
                  disabled={isSubmitting || !newSubtask.title.trim()}
                >
                  {isSubmitting ? 'Creating...' : 'Create'}
                </button>
                <button 
                  type="button" 
                  className="btn-cancel"
                  onClick={handleCancelCreate}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            /* Add subtask button */
            canEdit && canHaveChildren && (
              <button 
                className="add-subtask-btn"
                onClick={handleStartCreate}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                </svg>
                <span>Add {parentTask?.task_type === 'epic' ? 'child issue' : 'subtask'}</span>
              </button>
            )
          )}

          {/* Empty state */}
          {children.length === 0 && !isCreating && (
            <p className="subtask-empty">
              No {parentTask?.task_type === 'epic' ? 'child issues' : 'subtasks'} yet
            </p>
          )}
        </div>
      )}
      
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        variant={confirmModal.variant}
        isLoading={confirmModal.isLoading}
      />
    </div>
  );
};

export default SubtaskSection;
